import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Filter as FilterIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { adminService, setorService } from "../services/api";

const Setores = () => {
  const { user } = useAuth();

  const [setores, setSetores] = useState([]);
  const [escritorios, setEscritorios] = useState([]);
  const [loading, setLoading] = useState(false);

  // Estado para controlar visibilidade dos filtros
  const [mostrarFiltros, setMostrarFiltros] = useState(false);

  const [filtros, setFiltros] = useState({
    busca: "",
    escritorio_id_filter: "",
  });
  const [paginacao, setPaginacao] = useState({
    page: 0,
    limit: 10,
    total: 0,
  });

  // Estados para modal de setor
  const [modalAberto, setModalAberto] = useState(false);
  const [setorEditando, setSetorEditando] = useState(null);
  const [dadosSetor, setDadosSetor] = useState({
    descricao: "",
  });

  // Estados para notificações
  const [snackbar, setSnackbar] = useState({
    aberto: false,
    mensagem: "",
    tipo: "success",
  });

  // Estados para confirmação de exclusão
  const [confirmacaoExclusao, setConfirmacaoExclusao] = useState({
    aberto: false,
    setorId: null,
    setorDescricao: "",
  });

  // Estado para controlar loading dos botões
  const [salvando, setSalvando] = useState(false);
  const [excluindo, setExcluindo] = useState(false);

  const mostrarSnackbar = (mensagem, tipo = "success") => {
    setSnackbar({ aberto: true, mensagem, tipo });
  };

  const fecharSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, aberto: false }));
  };

  const carregarEscritorios = useCallback(async () => {
    try {
      const response = await adminService.getEscritorios();
      setEscritorios(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error("Erro ao carregar escritórios:", error);
    }
  }, []);

  const carregarSetores = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: paginacao.page + 1,
        limit: paginacao.limit,
        ...(filtros.busca && { busca: filtros.busca }),
        ...(filtros.escritorio_id_filter && {
          escritorio_id_filter: filtros.escritorio_id_filter,
        }),
      };

      const response = await setorService.getAll(params);
      setSetores(response.setores || []);
      setPaginacao((prev) => ({
        ...prev,
        total: response.paginacao?.totalItens || 0,
      }));
    } catch (error) {
      mostrarSnackbar("Erro ao carregar setores: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  }, [
    paginacao.page,
    paginacao.limit,
    filtros.busca,
    filtros.escritorio_id_filter,
  ]);

  useEffect(() => {
    carregarSetores();
  }, [carregarSetores]);

  useEffect(() => {
    if (user?.is_super_admin) {
      carregarEscritorios();
    }
  }, [carregarEscritorios, user]);

  const handleFiltroChange = (campo, valor) => {
    setFiltros((prev) => ({
      ...prev,
      [campo]: valor,
    }));
    setPaginacao((prev) => ({ ...prev, page: 0 }));
  };

  const handleChangePage = (event, newPage) => {
    setPaginacao((prev) => ({ ...prev, page: newPage }));
  };

  const handleChangeRowsPerPage = (event) => {
    setPaginacao((prev) => ({
      ...prev,
      limit: parseInt(event.target.value, 10),
      page: 0,
    }));
  };

  const handleInputChange = (campo, valor) => {
    setDadosSetor((prev) => ({
      ...prev,
      [campo]: valor,
    }));
  };

  const salvarSetor = async () => {
    if (salvando) return; // Evita cliques duplos

    try {
      setSalvando(true);

      if (!dadosSetor.descricao.trim()) {
        mostrarSnackbar("Descrição é obrigatória", "error");
        return;
      }

      if (dadosSetor.descricao.length > 64) {
        mostrarSnackbar("Descrição deve ter no máximo 64 caracteres", "error");
        return;
      }

      const dados = {
        descricao: dadosSetor.descricao.trim(),
      };

      if (setorEditando) {
        await setorService.update(setorEditando.id, dados);
        mostrarSnackbar("Setor atualizado com sucesso!");
      } else {
        await setorService.create(dados);
        mostrarSnackbar("Setor criado com sucesso!");
      }

      fecharModal();
      carregarSetores();
    } catch (error) {
      mostrarSnackbar(
        "Erro ao salvar setor: " + (error.message || "Erro desconhecido"),
        "error",
      );
    } finally {
      setSalvando(false);
    }
  };

  const abrirModalEdicao = (setor) => {
    setSetorEditando(setor);
    setDadosSetor({
      descricao: setor.descricao,
    });
    setModalAberto(true);
  };

  const abrirModalCriacao = () => {
    setSetorEditando(null);
    setDadosSetor({
      descricao: "",
    });
    setModalAberto(true);
  };

  const fecharModal = () => {
    setModalAberto(false);
    setSetorEditando(null);
    setDadosSetor({
      descricao: "",
    });
  };

  const abrirConfirmacaoExclusao = (setor) => {
    setConfirmacaoExclusao({
      aberto: true,
      setorId: setor.id,
      setorDescricao: setor.descricao,
    });
  };

  const fecharConfirmacaoExclusao = () => {
    setConfirmacaoExclusao({
      aberto: false,
      setorId: null,
      setorDescricao: "",
    });
  };

  const confirmarExclusao = async () => {
    if (excluindo) return; // Evita cliques duplos

    try {
      setExcluindo(true);
      await setorService.delete(confirmacaoExclusao.setorId);
      mostrarSnackbar("Setor excluído com sucesso!");
      carregarSetores();
      fecharConfirmacaoExclusao();
    } catch (error) {
      mostrarSnackbar(
        "Erro ao excluir setor: " + (error.message || "Erro desconhecido"),
        "error",
      );
    } finally {
      setExcluindo(false);
    }
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4" sx={{ fontWeight: 600, color: "#333" }}>
          Setores
        </Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<FilterIcon />}
            onClick={() => setMostrarFiltros(!mostrarFiltros)}
            sx={{ mr: 2 }}
          >
            Filtros
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={abrirModalCriacao}
            sx={{
              backgroundColor: "#1976d2",
              "&:hover": { backgroundColor: "#1565c0" },
            }}
          >
            Novo setor
          </Button>
        </Box>
      </Box>

      {/* Filtros */}
      {mostrarFiltros && (
        <Card
          sx={{
            mb: 3,
            p: 3,
            backgroundColor: "#f5f5f5",
            borderRadius: 1,
          }}
        >
          <Typography
            variant="h6"
            sx={{ mb: 2, color: "#333", fontWeight: 600 }}
          >
            Filtros
          </Typography>

          <Box
            sx={{
              display: "flex",
              gap: 2,
              width: "100%",
              flexWrap: { xs: "wrap", md: "nowrap" },
              alignItems: "center",
            }}
          >
            {/* Campo de Busca */}
            <Box
              sx={{
                flex: "0 1 50%",
                minWidth: { xs: "100%", md: "200px" },
                maxWidth: { md: "50%" },
              }}
            >
              <TextField
                fullWidth
                label="Buscar por descrição"
                placeholder="Ex: Contabilidade, Fiscal..."
                value={filtros.busca}
                onChange={(e) => handleFiltroChange("busca", e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  backgroundColor: "white",
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "white",
                  },
                }}
              />
            </Box>

            {/* Campo de Escritório - apenas para super admin */}
            {user?.is_super_admin && (
              <Box
                sx={{
                  flex: "0 1 40%",
                  minWidth: { xs: "100%", md: "200px" },
                  maxWidth: { md: "40%" },
                }}
              >
                <FormControl fullWidth size="medium">
                  <InputLabel>Escritório</InputLabel>
                  <Select
                    value={filtros.escritorio_id_filter}
                    label="Escritório"
                    onChange={(e) =>
                      handleFiltroChange("escritorio_id_filter", e.target.value)
                    }
                    sx={{
                      backgroundColor: "white",
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "white",
                      },
                    }}
                  >
                    <MenuItem value="">Todos os escritórios</MenuItem>
                    {escritorios.map((escritorio) => (
                      <MenuItem key={escritorio.id} value={escritorio.id}>
                        {escritorio.nome}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}
          </Box>

          {/* Botão para limpar filtros */}
          <Box sx={{ mt: 3 }}>
            <Button
              onClick={() => {
                setFiltros({
                  busca: "",
                  escritorio_id_filter: "",
                });
                setPaginacao((prev) => ({ ...prev, page: 0 }));
              }}
              variant="outlined"
              size="small"
            >
              Limpar filtros
            </Button>
          </Box>
        </Card>
      )}

      {/* Tabela */}
      <Card>
        <TableContainer component={Paper}>
          <Table sx={{ "& .MuiTableCell-root": { py: 1.5 } }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, py: 1.5 }}>
                  Descrição
                </TableCell>
                {user?.is_super_admin && (
                  <TableCell sx={{ fontWeight: 600, py: 1.5 }}>
                    Escritório
                  </TableCell>
                )}
                <TableCell
                  sx={{ fontWeight: 600, textAlign: "center", py: 1.5 }}
                >
                  Ações
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell
                    colSpan={user?.is_super_admin ? 3 : 2}
                    sx={{ textAlign: "center", py: 2 }}
                  >
                    Carregando...
                  </TableCell>
                </TableRow>
              ) : setores.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={user?.is_super_admin ? 3 : 2}
                    sx={{ textAlign: "center", py: 2 }}
                  >
                    Nenhum setor encontrado. Os setores são o primeiro cadastro
                    que deve ser feito para organizar as atividades do
                    escritório.
                  </TableCell>
                </TableRow>
              ) : (
                setores.map((setor) => (
                  <TableRow key={setor.id} hover>
                    <TableCell sx={{ py: 1.5 }}>{setor.descricao}</TableCell>
                    {user?.is_super_admin && (
                      <TableCell sx={{ py: 1.5 }}>
                        {setor.escritorio?.nome || "-"}
                      </TableCell>
                    )}
                    <TableCell sx={{ py: 1.5 }}>
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1,
                          justifyContent: "center",
                        }}
                      >
                        <Tooltip title="Editar">
                          <IconButton
                            size="small"
                            color="info"
                            onClick={() => abrirModalEdicao(setor)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Excluir">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => abrirConfirmacaoExclusao(setor)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={paginacao.total}
            page={paginacao.page}
            onPageChange={handleChangePage}
            rowsPerPage={paginacao.limit}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelRowsPerPage="Itens por página:"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
            }
          />
        </TableContainer>
      </Card>

      {/* Modal de Cadastro/Edição */}
      <Dialog open={modalAberto} onClose={fecharModal} maxWidth="sm" fullWidth>
        <DialogTitle>
          {setorEditando ? "Editar setor" : "Novo setor"}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Descrição"
            fullWidth
            variant="outlined"
            value={dadosSetor.descricao}
            onChange={(e) => handleInputChange("descricao", e.target.value)}
            inputProps={{ maxLength: 64 }}
            helperText={`${dadosSetor.descricao.length}/64 caracteres`}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={fecharModal}>Cancelar</Button>
          <Button onClick={salvarSetor} variant="contained" disabled={salvando}>
            {salvando ? (
              <CircularProgress size={20} />
            ) : setorEditando ? (
              "Atualizar"
            ) : (
              "Criar"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Confirmação de Exclusão */}
      <Dialog
        open={confirmacaoExclusao.aberto}
        onClose={fecharConfirmacaoExclusao}
      >
        <DialogTitle>Confirmar exclusão</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja excluir o setor "
            {confirmacaoExclusao.setorDescricao}"?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Esta ação não pode ser desfeita.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={fecharConfirmacaoExclusao} color="primary">
            Cancelar
          </Button>
          <Button
            onClick={confirmarExclusao}
            color="error"
            variant="contained"
            disabled={excluindo}
          >
            {excluindo ? <CircularProgress size={20} /> : "Excluir"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para notificações */}
      <Snackbar
        open={snackbar.aberto}
        autoHideDuration={6000}
        onClose={fecharSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={fecharSnackbar}
          severity={snackbar.tipo}
          sx={{ width: "100%" }}
        >
          {snackbar.mensagem}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Setores;
